import React, { useState, useEffect, useMemo } from "react";
import { Search, Plus, Minus, ShoppingBag, Check } from "lucide-react";
import { Product } from "../../types/product";
import { getAdminProducts } from "../../services/productsApi";
import GlobalLoader from "../ui/GlobalLoader";
import { cn } from "../../lib/utils";
import { useAuth } from "../../auth/AuthContext";

export interface SelectedProduct extends Product {
  quantity: number;
}

interface Props {
  /** Products already in the cart (to pre-fill quantities) */
  selectedProducts: SelectedProduct[];
  /** Called when user confirms their selection */
  onConfirm: (products: SelectedProduct[]) => void;
  /** Called to go back without saving */
  onBack: () => void;
}

const ProductSelectionStep: React.FC<Props> = ({
  selectedProducts,
  onConfirm,
  onBack,
}) => {
  const { user } = useAuth();
  const currencySymbol =
    user?.company?.currency_symbol ||
    user?.company?.currency?.currency_symbol ||
    "£";

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Local cart: id → quantity
  const [cart, setCart] = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    selectedProducts.forEach((p) => {
      if (p.quantity > 0) init[String(p.id)] = p.quantity;
    });
    return init;
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data = await getAdminProducts();
        if (mounted) setProducts(data);
      } catch {
        // silent — show empty list
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const isBarcodeSearch = useMemo(() => {
    const trimmed = search.trim();
    return /^\d{5}$/.test(trimmed);
  }, [search]);

  const filtered = useMemo(() => {
    if (!search.trim()) return products;
    const query = search.toLowerCase().trim();

    // 5-digit code: barcode/SKU exact match first, then contains (same as POS)
    if (isBarcodeSearch) {
      const trimmed = search.trim();
      return products.filter(
        (p) =>
          p.barcode?.trim() === trimmed ||
          p.sku?.trim() === trimmed ||
          p.barcode?.toLowerCase().includes(query) ||
          p.sku?.toLowerCase().includes(query)
      );
    }

    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        (p.category ?? "").toLowerCase().includes(query) ||
        (p.brand ?? "").toLowerCase().includes(query) ||
        (p.barcode ?? "").toLowerCase().includes(query) ||
        (p.sku ?? "").toLowerCase().includes(query)
    );
  }, [products, search, isBarcodeSearch]);

  const setQty = (id: string, delta: number) => {
    setCart((prev) => {
      const current = prev[id] ?? 0;
      const next = Math.max(0, current + delta);
      if (next === 0) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: next };
    });
  };

  const handleConfirm = () => {
    const result: SelectedProduct[] = Object.entries(cart)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => {
        const p = products.find((pr) => String(pr.id) === id)!;
        return { ...p, quantity: qty };
      });
    onConfirm(result);
  };

  const totalSelected = Object.values(cart).reduce((a, b) => a + b, 0);

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="px-4 sm:px-6 pt-4 pb-3">
        <div className="relative">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-[#885ABB] transition-colors"
          />
        </div>
      </div>

      {/* Product list */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-2 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <GlobalLoader
              initialPageLoad={false}
              compensateSidebar={false}
              description="Loading products..."
            />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 gap-2 text-gray-400">
            <ShoppingBag size={28} strokeWidth={1.5} />
            <p className="text-sm font-medium">No products found</p>
          </div>
        ) : (
          filtered.map((product) => {
            const qty = cart[String(product.id)] ?? 0;
            const inCart = qty > 0;
            const effectivePrice =
              product.discountedPrice ?? product.price;

            return (
              <div
                key={product.id}
                className={cn(
                  "flex items-center gap-3 bg-white rounded-xl p-3 border transition-all",
                  inCart
                    ? "border-[#885ABB]/40 shadow-sm"
                    : "border-gray-100"
                )}
              >
                {/* Image */}
                <div className="w-12 h-12 rounded-lg bg-gray-50 border border-gray-100 flex-shrink-0 overflow-hidden">
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ShoppingBag size={16} className="text-gray-300" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-[600] text-[#402B69] truncate">
                    {product.name}
                  </p>
                  <span className="text-sm font-bold text-[#885ABB]">
                    {currencySymbol}
                    {Number(effectivePrice).toFixed(2)}
                  </span>
                  {(product.sku || product.barcode) && (
                    <div className="text-[11px] text-[#402B69]/70 font-semibold mt-0.5 truncate">
                      {product.sku && <span>SKU: {product.sku}</span>}
                      {product.sku && product.barcode && (
                        <span className="mx-1">·</span>
                      )}
                      {product.barcode && (
                        <span>Barcode: {product.barcode}</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Qty controls */}
                {inCart ? (
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setQty(String(product.id), -1)}
                      className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center hover:border-[#885ABB] hover:text-[#885ABB] transition-colors"
                    >
                      <Minus size={12} />
                    </button>
                    <span className="w-6 text-center text-sm font-bold text-[#402B69]">
                      {qty}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQty(String(product.id), 1)}
                      className="w-7 h-7 rounded-lg bg-[#885ABB] text-white flex items-center justify-center hover:bg-[#7748A4] transition-colors"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setQty(String(product.id), 1)}
                    className="w-7 h-7 rounded-lg border-2 border-[#885ABB] text-[#885ABB] flex items-center justify-center hover:bg-[#885ABB] hover:text-white transition-all"
                  >
                    <Plus size={14} />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer actions */}
      <div className="px-4 sm:px-6 py-4 border-t border-gray-100 bg-white space-y-2">
        <button
          type="button"
          onClick={handleConfirm}
          className="w-full py-3 bg-[#885ABB] hover:bg-[#7748A4] text-white font-bold text-sm rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          <Check size={16} />
          {totalSelected > 0
            ? `Add ${totalSelected} product${totalSelected > 1 ? "s" : ""} to booking`
            : "Confirm (no products)"}
        </button>
        <button
          type="button"
          onClick={onBack}
          className="w-full py-2.5 text-sm font-semibold text-gray-500 hover:text-[#885ABB] transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ProductSelectionStep;
